// src/components/roads/RoadPublicReportsPanel.js
// --- UPDATE (Phone AI Helpdesk) "Public Reports" tab of the Road Works page.
// Every pothole / road damage / vehicle-damage / debris / sidewalk / signal report the public phoned in
// (24-hour AI line or a helpdesk agent), so road managers and supervisors see them next to the network:
// severity, how many callers, where, which road, inspection and work-order state — and act on them
// (attach a road, schedule the inspection, raise the work order, verify true / false, open the full trace).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, HStack, VStack, Text, Badge, Button, Table, Thead, Tbody, Tr, Th, Td, Select, Spinner, Tooltip, Icon,
  useColorModeValue, useToast, Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  useDisclosure, Alert, AlertIcon, SimpleGrid, Stat, StatLabel, StatNumber, Switch, FormControl, FormLabel, Wrap, WrapItem, Input,
} from '@chakra-ui/react';
import { MdPhoneInTalk, MdRefresh, MdAddRoad, MdEventNote, MdBuild, MdOpenInNew, MdMap } from 'react-icons/md';
import webSocketService from '../../services/websocketService';
import phoneHelpdeskApi, { CATEGORY_META, STAGE_LABEL, SEVERITY_COLOR, wsPayload } from '../../services/phoneHelpdeskApi';
import ReportTracePanel from '../helpdesk/ReportTracePanel';
import PublicReportsMap from '../helpdesk/PublicReportsMap';

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const LIVE_EVENTS = new Set(['phone_report_created', 'phone_report_geotagged', 'report_verified', 'ticket_resolved', 'work_order_linked', 'inspection_scheduled', 'inspection_completed']);

function RoadPublicReportsPanel({ roads = [], onOpenRoad, onCount, focusIssueId = null }) {
  const toast = useToast();
  const drawer = useDisclosure();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [days, setDays] = useState(60);
  const [needsRoadOnly, setNeedsRoadOnly] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [traceId, setTraceId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [roadPick, setRoadPick] = useState({});
  const [mapKey, setMapKey] = useState(0);
  const timer = useRef(null);
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;
  const border = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const focusBg = useColorModeValue('yellow.50', 'whiteAlpha.100');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const d = await phoneHelpdeskApi.roadFeed({ days, open: openOnly ? undefined : 0 });
      const list = Array.isArray(d) ? d : [];
      setRows(list);
      setError(null);
      try { onCountRef.current?.(list.filter((r) => !['resolved', 'closed', 'rejected'].includes(r.lifecycle_stage)).length); } catch (_) { /* optional */ }
    } catch (e) {
      setError(e.response?.status === 404 ? 'The phone helpdesk API is not installed on the server yet.' : (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [days, openOnly]);
  useEffect(() => { load(false); }, [load]);

  // live: new phone reports, verdicts, inspections, work orders and Road-module issue events
  useEffect(() => {
    const kick = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { load(true); setMapKey((k) => k + 1); }, 1000); };
    const onHelpdesk = (raw) => { const m = wsPayload(raw); if (m && LIVE_EVENTS.has(m.event)) kick(); };
    const onRoadIssue = () => kick();
    webSocketService.connect(); // idempotent
    const offs = [
      webSocketService.subscribe('helpdesk', onHelpdesk),
      webSocketService.subscribe('road_issue_reported', onRoadIssue),
      // crews updating the linked work orders (dispatched / in progress / completed)
      webSocketService.subscribe('work_order_events', (raw) => { const p = wsPayload(raw); if (p && ['status_updated', 'assigned', 'created'].includes(p.type)) kick(); }),
      webSocketService.subscribe('status', (st) => { if (st?.status === 'connected') kick(); }), // catch up after a reconnect
    ];
    return () => {
      offs.forEach((off) => { try { off(); } catch (_) { /* already removed */ } });
      clearTimeout(timer.current);
    };
  }, [load]);

  // deep link ?tab=public&issue=<road_issue id>
  useEffect(() => {
    if (!focusIssueId || !rows.length) return;
    const r = rows.find((x) => String(x.road_issue_id) === String(focusIssueId));
    if (r) { setTraceId(r.report_id); drawer.onOpen(); }
  }, [focusIssueId, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => (needsRoadOnly ? rows.filter((r) => r.needs_road) : rows), [rows, needsRoadOnly]);
  const kpi = useMemo(() => ({
    open: rows.filter((r) => !['resolved', 'closed', 'rejected'].includes(r.lifecycle_stage)).length,
    critical: rows.filter((r) => r.severity === 'critical' || r.hazard_to_life).length,
    needsRoad: rows.filter((r) => r.needs_road).length,
    noWo: rows.filter((r) => !r.work_order_id && !['resolved', 'closed', 'rejected'].includes(r.lifecycle_stage)).length,
    vehicle: rows.filter((r) => r.vehicle_damage || r.category === 'vehicle_damage').length,
  }), [rows]);

  const act = async (row, label, fn) => {
    setBusyId(row.report_id);
    try {
      await fn();
      toast({ title: label, status: 'success', duration: 2500, isClosable: true });
      await load(true);
      setMapKey((k) => k + 1);
    } catch (e) {
      toast({ title: `${label} failed`, description: e.response?.data?.message || e.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setBusyId(null);
    }
  };

  const openTrace = (row) => { setTraceId(row.report_id); drawer.onOpen(); };

  return (
    <Box>
      <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
        <HStack><Icon as={MdPhoneInTalk} color="green.500" boxSize={5} /><Text fontWeight="800">Public road reports (24-hour phone line & helpdesk)</Text></HStack>
        <HStack>
          <Button size="xs" leftIcon={<Icon as={MdMap} />} variant={showMap ? 'solid' : 'outline'} onClick={() => setShowMap((v) => !v)}>Map</Button>
          <Button size="xs" leftIcon={<Icon as={MdRefresh} />} onClick={() => load(false)} isLoading={loading}>Refresh</Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={3}>
        {[['Open reports', kpi.open, 'orange.400'], ['Critical / hazard', kpi.critical, 'red.400'], ['Need a road', kpi.needsRoad, 'purple.400'], ['No work order yet', kpi.noWo, 'blue.400'], ['Vehicle damage claims', kpi.vehicle, 'pink.400']].map(([l, v, c]) => (
          <Stat key={l} p={3} borderWidth="1px" borderColor={border} borderRadius="xl"><StatLabel fontSize="2xs">{l}</StatLabel><StatNumber fontSize="lg" color={c}>{loading ? '—' : v}</StatNumber></Stat>
        ))}
      </SimpleGrid>

      <Wrap spacing={3} mb={3} align="center">
        <WrapItem><Select size="xs" w="120px" value={days} onChange={(e) => setDays(Number(e.target.value))}><option value={7}>7 days</option><option value={30}>30 days</option><option value={60}>60 days</option><option value={180}>180 days</option><option value={365}>1 year</option></Select></WrapItem>
        <WrapItem><FormControl display="flex" alignItems="center"><FormLabel mb={0} fontSize="xs">Open only</FormLabel><Switch size="sm" isChecked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /></FormControl></WrapItem>
        <WrapItem><FormControl display="flex" alignItems="center"><FormLabel mb={0} fontSize="xs">Needs a road</FormLabel><Switch size="sm" isChecked={needsRoadOnly} onChange={(e) => setNeedsRoadOnly(e.target.checked)} /></FormControl></WrapItem>
      </Wrap>

      {showMap && <Box mb={3}><PublicReportsMap height="380px" refreshKey={mapKey} onSelectReport={(p) => { setTraceId(p.id); drawer.onOpen(); }} /></Box>}
      {error && <Alert status="warning" borderRadius="lg" mb={3} fontSize="sm"><AlertIcon />{error}</Alert>}

      {loading ? <HStack p={4}><Spinner size="sm" /><Text fontSize="sm">Loading public reports…</Text></HStack> : shown.length === 0 ? (
        <Text p={4} fontSize="sm" color="gray.500">No public road reports{openOnly ? ' open' : ''} in this period.</Text>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderColor={border} borderRadius="xl">
          <Table size="sm">
            <Thead bg={headerBg}><Tr><Th>Ticket</Th><Th>Report</Th><Th>Where</Th><Th>Road</Th><Th>Callers</Th><Th>Inspection</Th><Th>Work order</Th><Th>Stage</Th><Th>Received</Th><Th /></Tr></Thead>
            <Tbody>
              {shown.map((r) => {
                const meta = CATEGORY_META[r.category] || { label: r.category, color: 'gray' };
                const busy = busyId === r.report_id;
                const done = ['resolved', 'closed', 'rejected'].includes(r.lifecycle_stage);
                return (
                  <Tr key={r.report_id} bg={focusIssueId && String(r.road_issue_id) === String(focusIssueId) ? focusBg : undefined}>
                    <Td fontWeight="700" fontSize="xs"><Button size="xs" variant="link" onClick={() => openTrace(r)}>{r.ticket_number}</Button></Td>
                    <Td>
                      <HStack spacing={1} flexWrap="wrap">
                        <Badge colorScheme={meta.color}>{meta.label}</Badge>
                        <Badge colorScheme={SEVERITY_COLOR[r.severity]}>{r.severity}</Badge>
                        {!!r.hazard_to_life && <Badge colorScheme="red" variant="solid">hazard</Badge>}
                        {r.verification_status === 'confirmed_true' && <Badge colorScheme="green">verified</Badge>}
                      </HStack>
                      <Tooltip label={r.description} hasArrow><Text fontSize="2xs" color="gray.500" noOfLines={1} maxW="240px">{r.description}</Text></Tooltip>
                    </Td>
                    <Td maxW="200px"><Text fontSize="xs" noOfLines={2}>{r.location_text || '—'}</Text><Text fontSize="2xs" color="gray.500">{r.latitude != null ? `${Number(r.latitude).toFixed(5)}, ${Number(r.longitude).toFixed(5)}` : 'waiting for caller GPS'}</Text></Td>
                    <Td>
                      {r.matched_road_id ? (
                        <Button size="xs" variant="link" colorScheme="blue" rightIcon={<Icon as={MdOpenInNew} />} onClick={() => onOpenRoad && onOpenRoad({ id: r.matched_road_id, name: r.road_name })}>{r.road_name || r.matched_road_id}</Button>
                      ) : (
                        <HStack spacing={1}>
                          {roads.length > 0 ? (
                            <Select size="xs" w="150px" placeholder="Pick road…" value={roadPick[r.report_id] || ''} onChange={(e) => setRoadPick({ ...roadPick, [r.report_id]: e.target.value })}>
                              {roads.map((rd) => <option key={rd.id} value={rd.id}>{rd.name || rd.id}</option>)}
                            </Select>
                          ) : (
                            <Input size="xs" w="150px" placeholder="Road asset id" value={roadPick[r.report_id] || ''} onChange={(e) => setRoadPick({ ...roadPick, [r.report_id]: e.target.value })} />
                          )}
                          <Tooltip label="Attach road → logs it on Road Reports and schedules the inspection"><Button size="xs" colorScheme="purple" leftIcon={<Icon as={MdAddRoad} />} isDisabled={!roadPick[r.report_id] || busy} isLoading={busy} onClick={() => act(r, 'Road attached', () => phoneHelpdeskApi.attachRoad(r.report_id, roadPick[r.report_id]))}>Attach</Button></Tooltip>
                        </HStack>
                      )}
                    </Td>
                    <Td fontWeight={Number(r.cluster_callers) >= 3 ? '800' : 'normal'}>{r.cluster_callers || 1}</Td>
                    <Td fontSize="xs">{r.road_inspection_id ? <Badge colorScheme={r.inspection_status === 'completed' ? 'green' : 'yellow'}>{r.inspection_status || 'scheduled'}{r.inspection_date ? ` · ${new Date(r.inspection_date).toLocaleDateString()}` : ''}</Badge> : (
                      <Button size="xs" variant="outline" leftIcon={<Icon as={MdEventNote} />} isDisabled={!r.matched_road_id || busy || done} onClick={() => act(r, 'Inspection scheduled', () => phoneHelpdeskApi.scheduleInspection(r.report_id, {}))}>Schedule</Button>
                    )}</Td>
                    <Td fontSize="xs">{r.work_order_id ? <Badge colorScheme={['completed', 'closed'].includes(r.work_order_status) ? 'green' : 'blue'}>{r.work_order_status || 'linked'}</Badge> : (
                      <Button size="xs" colorScheme="blue" leftIcon={<Icon as={MdBuild} />} isDisabled={busy || done} isLoading={busy} onClick={() => act(r, 'Work order created', () => phoneHelpdeskApi.createWorkOrder(r.report_id))}>Raise</Button>
                    )}</Td>
                    <Td><Badge variant="outline" fontSize="2xs">{STAGE_LABEL[r.lifecycle_stage] || r.lifecycle_stage}</Badge></Td>
                    <Td fontSize="2xs">{fmt(r.created_at)}</Td>
                    <Td><Button size="xs" variant="ghost" onClick={() => openTrace(r)}>Trace</Button></Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <Drawer isOpen={drawer.isOpen} onClose={drawer.onClose} size="xl" placement="right">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Public report — start to finish</DrawerHeader>
          <DrawerBody py={4}>
            <VStack align="stretch">
              {traceId && <ReportTracePanel reportId={traceId} onChanged={() => { load(true); setMapKey((k) => k + 1); }} />}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

RoadPublicReportsPanel.propTypes = {
  roads: PropTypes.arrayOf(PropTypes.object),
  onOpenRoad: PropTypes.func,
  onCount: PropTypes.func,
  focusIssueId: PropTypes.string,
};

export default React.memo(RoadPublicReportsPanel);
// --- END UPDATE
